"use client";

import Image from "next/image";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import LogoUploadCard from "./LogoUploadCard";
import type { AppLanguage } from "../constants/dashboard-language";
import {
  DEFAULT_LANDING_PAGE_CONFIG,
  LANDING_PAGE_STORAGE_KEY,
  mergeLandingPageConfig,
  type LandingPageConfig,
  type LandingElementPositionKey,
  type LandingImagePositionKey,
} from "../constants/landing-page-config";
import { coverImage, staticAssets } from "../homepage/components/homepage-shared";
import { HomeStyles } from "../homepage/components/HomeStyles";

type LandingPageSectionProps = {
  language: AppLanguage;
};

type CopyKey = Exclude<keyof LandingPageConfig["images"], "gallery">;
type ImageCardKey = Exclude<CopyKey, "logo" | "heroCover">;
type PreviewBoxKey = "logo" | "subtitle" | "title" | "names" | "date" | "button";
type PreviewPosition = { x: number; y: number };
type TextFieldKey = Exclude<keyof LandingPageConfig, "images" | "imagePositions" | "elementPositions" | "elementSizes" | "timeline">;
type EditorFieldKey = TextFieldKey | "coupleBlock" | "titleBlock";
type TextFieldFormat = {
  fontFamily: string;
  fontSize: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  highlightColor: string;
  fontColor: string;
  alignment: "Left" | "Center" | "Right";
};
type CoupleBlockFieldKey = "groom" | "bride";
const COUPLE_BLOCK_KEYS: CoupleBlockFieldKey[] = ["groom", "bride"];
const clampImagePosition = (value: number) => Math.min(100, Math.max(0, value));

const labels = {
  en: {
    title: "Landing Page Content",
    subtitle:
      "Upload images and update the wedding invitation text shown on `/homepage`.",
    save: "Save changes",
    reset: "Reset to default",
    preview: "Open homepage preview",
    saved: "Landing page content saved.",
    imageHint: "PNG, JPG, or WebP works best.",
    timelineTitle: "Schedule",
    galleryTitle: "Gallery",
    textsTitle: "Main Text",
    imagesTitle: "Images",
    logoImage: "Logo",
    coverImage: "Cover image",
    featureImage: "Feature card image",
    portraitImage: "Main portrait",
    invitationImage: "Invitation section image",
    venueImage: "Venue section image",
    brideQr: "Bride KHQR",
    groomQr: "Groom KHQR",
    upload: "Upload image",
    remove: "Use default",
  },
  kh: {
    title: "កែទំព័រ Landing Page",
    subtitle: "ផ្ទុករូបភាព និងកែអត្ថបទសម្រាប់ទំព័រ `/homepage` ។",
    save: "រក្សាទុក",
    reset: "ត្រឡប់លំនាំដើម",
    preview: "បើកមើល Homepage",
    saved: "បានរក្សាទុកទិន្នន័យ Landing page។",
    imageHint: "គាំទ្រ PNG, JPG និង WebP។",
    timelineTitle: "កម្មវិធី",
    galleryTitle: "កម្រងរូបភាព",
    textsTitle: "អត្ថបទសំខាន់",
    imagesTitle: "រូបភាព",
    logoImage: "ឡូហ្គោ",
    coverImage: "រូបភាពគម្រប",
    featureImage: "រូបភាពកាតខាងឆ្វេង",
    portraitImage: "រូបភាពគូស្វាមីភរិយា",
    invitationImage: "រូបភាពផ្នែកអញ្ជើញ",
    venueImage: "រូបភាពផ្នែកទីតាំង",
    brideQr: "KHQR កូនស្រី",
    groomQr: "KHQR កូនប្រុស",
    upload: "ផ្ទុករូបភាព",
    remove: "ប្រើលំនាំដើម",
  },
} as const;

const imageFieldLabels: Record<ImageCardKey, keyof (typeof labels)["en"]> = {
  featureImage: "featureImage",
  mainPortrait: "portraitImage",
  invitationImage: "invitationImage",
  venueImage: "venueImage",
  khqrOne: "brideQr",
  khqrTwo: "groomQr",
};

const textFields: Array<{
  key: TextFieldKey;
  labelEn: string;
  labelKh: string;
  multiline?: boolean;
}> = [
  { key: "subtitleKh", labelEn: "Title", labelKh: "ចំណងជើង" },
  { key: "titleKh", labelEn: "Subtitle", labelKh: "ចំណងជើងរង" },
  { key: "groom", labelEn: "Groom name", labelKh: "ឈ្មោះកូនប្រុស" },
  { key: "bride", labelEn: "Bride name", labelKh: "ឈ្មោះកូនស្រី" },
  { key: "dateLabel", labelEn: "Date text", labelKh: "អត្ថបទកាលបរិច្ឆេទ" },
  { key: "timeLabel", labelEn: "Time text", labelKh: "អត្ថបទពេលវេលា" },
  { key: "venueKh", labelEn: "Venue text", labelKh: "អត្ថបទទីតាំង" },
  { key: "mapUrl", labelEn: "Google Maps URL", labelKh: "តំណផែនទី" },
  { key: "musicUrl", labelEn: "Music URL", labelKh: "តំណភ្លេង" },
  {
    key: "invitationMessage",
    labelEn: "Invitation message",
    labelKh: "អត្ថបទអញ្ជើញ",
    multiline: true,
  },
  {
    key: "thankYouMessage",
    labelEn: "Thank-you message",
    labelKh: "អត្ថបទថ្លែងអំណរគុណ",
    multiline: true,
  },
];

const DEFAULT_TEXT_FIELD_FORMAT: TextFieldFormat = {
  fontFamily: "Aptos (Body)",
  fontSize: "12",
  bold: false,
  italic: false,
  underline: false,
  highlightColor: "#facc15",
  fontColor: "#ffffff",
  alignment: "Center",
};

const toDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(new Error("Unable to read image file."));
    reader.readAsDataURL(file);
  });

export default function LandingPageSection({ language }: LandingPageSectionProps) {
  const copy = labels[language];
  const [draft, setDraft] = useState<LandingPageConfig>(() => {
    if (typeof window === "undefined") {
      return DEFAULT_LANDING_PAGE_CONFIG;
    }
    try {
      const raw = window.localStorage.getItem(LANDING_PAGE_STORAGE_KEY);
      return mergeLandingPageConfig(raw ? JSON.parse(raw) : null);
    } catch {
      return DEFAULT_LANDING_PAGE_CONFIG;
    }
  });
  const [savedNotice, setSavedNotice] = useState("");
  const [namesSeparator, setNamesSeparator] = useState("");
  const [hasNamesSeparator, setHasNamesSeparator] = useState(false);
  const [activeTextField, setActiveTextField] = useState<EditorFieldKey | null>(null);
  const [fieldFormats, setFieldFormats] = useState<Record<TextFieldKey, TextFieldFormat>>(() =>
    textFields.reduce(
      (acc, field) => {
        acc[field.key] = { ...DEFAULT_TEXT_FIELD_FORMAT };
        return acc;
      },
      {} as Record<TextFieldKey, TextFieldFormat>,
    ),
  );
  const [previewPositions, setPreviewPositions] = useState<Record<PreviewBoxKey, PreviewPosition>>({
    logo: { x: 0, y: 0 },
    subtitle: { x: 0, y: 0 },
    title: { x: 0, y: 0 },
    names: { x: 0, y: 0 },
    date: { x: 0, y: 0 },
    button: { x: 0, y: 0 },
  });
  const dragStateRef = useRef<{
    key: PreviewBoxKey;
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const imageDragStateRef = useRef<{
    key: LandingImagePositionKey;
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    width: number;
    height: number;
  } | null>(null);
  const coverPreviewRef = useRef<HTMLDivElement | null>(null);
  const elementDragStateRef = useRef<{
    key: LandingElementPositionKey;
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    width: number;
    height: number;
  } | null>(null);
  const textInputRefs = useRef<
    Partial<Record<TextFieldKey, HTMLInputElement | HTMLTextAreaElement | null>>
  >({});
  const titleBlockRef = useRef<HTMLTextAreaElement | null>(null);
  const coupleBlockRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!savedNotice) return;
    const timer = window.setTimeout(() => setSavedNotice(""), 2200);
    return () => window.clearTimeout(timer);
  }, [savedNotice]);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const drag = dragStateRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        const imageDrag = imageDragStateRef.current;
        const elementDrag = elementDragStateRef.current;
        if (imageDrag?.pointerId === event.pointerId) {
          event.preventDefault();
          setDraft((current) => ({
            ...current,
            imagePositions: {
              ...current.imagePositions,
              [imageDrag.key]: {
                x: clampImagePosition(imageDrag.originX + ((event.clientX - imageDrag.startX) / imageDrag.width) * 100),
                y: clampImagePosition(imageDrag.originY + ((event.clientY - imageDrag.startY) / imageDrag.height) * 100),
              },
            },
          }));
          return;
        }

        if (elementDrag?.pointerId === event.pointerId) {
          event.preventDefault();
          setDraft((current) => ({
            ...current,
            elementPositions: {
              ...current.elementPositions,
              [elementDrag.key]: {
                x: clampImagePosition(elementDrag.originX + ((event.clientX - elementDrag.startX) / elementDrag.width) * 100),
                y: clampImagePosition(elementDrag.originY + ((event.clientY - elementDrag.startY) / elementDrag.height) * 100),
              },
            },
          }));
          return;
        }

        return;
      }

      event.preventDefault();
      setPreviewPositions((current) => ({
        ...current,
        [drag.key]: {
          x: drag.originX + (event.clientX - drag.startX),
          y: drag.originY + (event.clientY - drag.startY),
        },
      }));
    };

    const handlePointerUp = (event: PointerEvent) => {
      const drag = dragStateRef.current;
      const imageDrag = imageDragStateRef.current;
      const elementDrag = elementDragStateRef.current;
      if (drag?.pointerId === event.pointerId) {
        dragStateRef.current = null;
      }
      if (imageDrag?.pointerId === event.pointerId) {
        imageDragStateRef.current = null;
      }
      if (elementDrag?.pointerId === event.pointerId) {
        elementDragStateRef.current = null;
      }
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, []);

  const gallerySlots = useMemo(() => {
    const gallery = [...draft.images.gallery];
    while (gallery.length < 6) {
      gallery.push(DEFAULT_LANDING_PAGE_CONFIG.images.gallery[gallery.length] ?? DEFAULT_LANDING_PAGE_CONFIG.images.gallery[0]);
    }
    return gallery.slice(0, 6);
  }, [draft.images.gallery]);

  const handleTextChange = (
    key: TextFieldKey,
    value: string,
  ) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const coupleBlockValue = useMemo(
    () => (hasNamesSeparator ? [draft.groom, namesSeparator, draft.bride] : [draft.groom, draft.bride]).join("\n"),
    [draft.groom, draft.bride, hasNamesSeparator, namesSeparator],
  );
  const titleBlockValue = useMemo(() => [draft.subtitleKh, draft.titleKh].join("\n"), [draft.subtitleKh, draft.titleKh]);

  const handleTextFieldFocus = (key: EditorFieldKey) => () => {
    setActiveTextField(key);
  };

  const handleCoupleBlockChange = (value: string) => {
    const lines = value.split("\n");
    const firstContentIndex = lines.findIndex((line) => line.trim().length > 0);
    const lastContentIndex = lines.findLastIndex((line) => line.trim().length > 0);
    const groom = firstContentIndex >= 0 ? lines[firstContentIndex] : "";
    const bride =
      lastContentIndex >= 0 && lastContentIndex !== firstContentIndex ? lines[lastContentIndex] : "";
    const separatorLines =
      firstContentIndex >= 0 && lastContentIndex > firstContentIndex
        ? lines.slice(firstContentIndex + 1, lastContentIndex)
        : [];
    const separator = separatorLines.join("\n");

    setHasNamesSeparator(separatorLines.length > 0);
    setNamesSeparator(separator);
    setDraft((prev) => ({
      ...prev,
      groom,
      bride,
    }));
  };

  const handleTitleBlockChange = (value: string) => {
    const [subtitleKh = "", ...titleParts] = value.split("\n");
    setDraft((prev) => ({
      ...prev,
      subtitleKh,
      titleKh: titleParts.join("\n"),
    }));
  };

  const handleImageUpload = async (
    event: ChangeEvent<HTMLInputElement>,
    key: CopyKey,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const dataUrl = await toDataUrl(file);
    setDraft((prev) => ({
      ...prev,
      images: {
        ...prev.images,
        [key]: dataUrl,
      },
      imagePositions: {
        ...prev.imagePositions,
        [key]: DEFAULT_LANDING_PAGE_CONFIG.imagePositions[key],
      },
    }));
    event.target.value = "";
  };

  const handleGalleryUpload = async (
    event: ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const dataUrl = await toDataUrl(file);
    setDraft((prev) => {
      const nextGallery = [...prev.images.gallery];
      nextGallery[index] = dataUrl;
      return {
        ...prev,
        images: {
          ...prev.images,
          gallery: nextGallery,
        },
      };
    });
    event.target.value = "";
  };

  const handleSave = () => {
    window.localStorage.setItem(LANDING_PAGE_STORAGE_KEY, JSON.stringify(draft));
    setSavedNotice(copy.saved);
  };

  const handleReset = () => {
    window.localStorage.setItem(
      LANDING_PAGE_STORAGE_KEY,
      JSON.stringify(DEFAULT_LANDING_PAGE_CONFIG),
    );
    setDraft(DEFAULT_LANDING_PAGE_CONFIG);
    setFieldFormats(
      textFields.reduce(
        (acc, field) => {
          acc[field.key] = { ...DEFAULT_TEXT_FIELD_FORMAT };
          return acc;
        },
        {} as Record<TextFieldKey, TextFieldFormat>,
      ),
    );
    setSavedNotice(copy.saved);
  };

  const subtitleFormat = fieldFormats.subtitleKh;
  const titleFormat = fieldFormats.titleKh;
  const groomFormat = fieldFormats.groom;
  const brideFormat = fieldFormats.bride;
  const dateLabelFormat = fieldFormats.dateLabel;

  const startPreviewDrag =
    (key: PreviewBoxKey) => (event: ReactPointerEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.preventDefault();
      const current = previewPositions[key];
      dragStateRef.current = {
        key,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: current.x,
        originY: current.y,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    };

  const startImagePositionDrag =
    (key: LandingImagePositionKey) => (event: ReactPointerEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.preventDefault();
      const current = draft.imagePositions[key] ?? DEFAULT_LANDING_PAGE_CONFIG.imagePositions[key];
      const rect = event.currentTarget.getBoundingClientRect();
      imageDragStateRef.current = {
        key,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: current.x,
        originY: current.y,
        width: Math.max(rect.width, 1),
        height: Math.max(rect.height, 1),
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    };

  const startElementPositionDrag =
    (key: LandingElementPositionKey) => (event: ReactPointerEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.preventDefault();
      const current = draft.elementPositions[key] ?? DEFAULT_LANDING_PAGE_CONFIG.elementPositions[key];
      const rect = coverPreviewRef.current?.getBoundingClientRect();
      if (!rect) return;

      elementDragStateRef.current = {
        key,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: current.x,
        originY: current.y,
        width: Math.max(rect.width, 1),
        height: Math.max(rect.height, 1),
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    };

  return (
    <div className="grid gap-6">
      <section className="rounded-[2rem] border border-[color:var(--panel-border)] bg-[color:var(--card)] p-5 shadow-[var(--panel-shadow-soft)] sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[color:var(--accent)]">
              Homepage CMS
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-[color:var(--foreground)]">
              {copy.title}
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-7 text-[color:var(--muted)]">
              {copy.subtitle}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => window.open("/homepage", "_blank", "noopener,noreferrer")}
              className="rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-3 text-sm font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
            >
              {copy.preview}
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-3 text-sm font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
            >
              {copy.reset}
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="rounded-full bg-[color:var(--accent)] px-5 py-3 text-sm font-semibold text-[color:var(--accent-contrast)] transition hover:brightness-110"
            >
              {copy.save}
            </button>
          </div>
        </div>
        {savedNotice ? (
          <p className="mt-4 rounded-2xl bg-[color:var(--surface)] px-4 py-3 text-sm text-[color:var(--foreground)]">
            {savedNotice}
          </p>
        ) : null}
      </section>

      <section className="grid items-start gap-6 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <div className="h-fit rounded-[2rem] border border-[color:var(--panel-border)] bg-[color:var(--card)] p-5 shadow-[var(--panel-shadow-soft)] sm:p-6">
          <h3 className="text-lg font-semibold text-[color:var(--foreground)]">
            {copy.imagesTitle}
          </h3>
          <p className="mt-2 text-sm text-[color:var(--muted)]">{copy.imageHint}</p>
          <div className="mt-5 w-full rounded-[2rem] bg-[radial-gradient(circle_at_top,rgba(255,246,230,0.72),transparent_30%),radial-gradient(circle_at_bottom,rgba(255,236,209,0.42),transparent_36%),linear-gradient(180deg,rgba(255,249,241,0.44),rgba(246,230,211,0.58))] p-2 shadow-[0_20px_45px_rgba(47,29,15,0.12)] backdrop-blur-[4px]">
            <div
              ref={coverPreviewRef}
              className="cover-stage relative aspect-[3/4] w-full cursor-grab overflow-hidden rounded-[2rem] border border-white/18 bg-black touch-none active:cursor-grabbing"
              onPointerDown={startImagePositionDrag("heroCover")}
            >
              <Image
                alt="Homepage cover preview"
                className="absolute inset-0 h-full w-full scale-110 object-cover blur-[2px]"
                fill
                sizes="(max-width: 1280px) 100vw, 30vw"
                src={coverImage}
                style={{
                  objectPosition: `${draft.imagePositions.heroCover.x}% ${draft.imagePositions.heroCover.y}%`,
                }}
              />
              <Image
                alt="Homepage cover preview"
                className="absolute inset-0 h-full w-full object-cover"
                fill
                sizes="(max-width: 1280px) 100vw, 30vw"
                src={coverImage}
                style={{
                  objectPosition: `${draft.imagePositions.heroCover.x}% ${draft.imagePositions.heroCover.y}%`,
                }}
              />
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(20,11,7,0.18),rgba(28,15,9,0.68))]" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,236,204,0.18),transparent_30%),radial-gradient(circle_at_bottom,rgba(0,0,0,0.22),transparent_42%),linear-gradient(135deg,rgba(255,255,255,0.06),transparent_42%,rgba(0,0,0,0.14))]" />

              <div className="relative z-10 flex h-full flex-col justify-between px-5 py-5 text-center text-[#f9ead0]">
                <div
                  className="absolute z-30 cursor-grab touch-none select-none active:cursor-grabbing"
                  onPointerDown={startElementPositionDrag("logo")}
                  style={{
                    left: `${draft.elementPositions.logo.x}%`,
                    top: `${draft.elementPositions.logo.y}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <div className="flex justify-start">
                    <div
                      className="flex items-center justify-center overflow-hidden rounded-full border border-white/18 bg-white/95 p-1 shadow-[0_18px_45px_rgba(14,7,3,0.28)]"
                      style={{
                        width: `${draft.elementSizes.logo}px`,
                        height: `${draft.elementSizes.logo}px`,
                      }}
                    >
                      <div className="relative h-full w-full overflow-hidden rounded-full">
                        <img
                          alt="Wedding mark"
                          className="h-full w-full rounded-full object-cover"
                          draggable={false}
                          src={draft.images.logo || staticAssets.weddingMark}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="absolute left-5 top-[5rem] z-20 w-[calc(100%-2.5rem)] cursor-grab touch-none active:cursor-grabbing"
                  onPointerDown={startPreviewDrag("subtitle")}
                  style={{ transform: `translate(${previewPositions.subtitle.x}px, ${previewPositions.subtitle.y}px)` }}
                >
                  <p
                    className="cover-text-outline pt-1 text-center font-khmer text-[1.32rem] tracking-[0.16em] text-white"
                    style={{
                      fontFamily: subtitleFormat.fontFamily,
                      fontSize: "1.32rem",
                      fontStyle: subtitleFormat.italic ? "italic" : "normal",
                      fontWeight: subtitleFormat.bold ? "700" : "400",
                      textDecoration: subtitleFormat.underline ? "underline" : "none",
                      textAlign: subtitleFormat.alignment.toLowerCase() as "left" | "center" | "right",
                    }}
                  >
                    {draft.subtitleKh}
                  </p>
                </div>

                <div
                  className="absolute left-5 top-[7.6rem] z-20 w-[calc(100%-2.5rem)] cursor-grab touch-none active:cursor-grabbing"
                  onPointerDown={startPreviewDrag("title")}
                  style={{ transform: `translate(${previewPositions.title.x}px, ${previewPositions.title.y}px)` }}
                >
                  <h4
                    className="cover-title-3d font-khmer text-[1.74rem] leading-[1.6] text-white"
                    style={{
                      fontFamily: titleFormat.fontFamily,
                      fontSize: "1.74rem",
                      fontStyle: titleFormat.italic ? "italic" : "normal",
                      fontWeight: titleFormat.bold ? "700" : "500",
                      textDecoration: titleFormat.underline ? "underline" : "none",
                      textAlign: titleFormat.alignment.toLowerCase() as "left" | "center" | "right",
                    }}
                  >
                    {draft.titleKh}
                  </h4>
                </div>

                <div
                  className="absolute left-5 top-[10.9rem] z-20 w-[calc(100%-2.5rem)] cursor-grab touch-none active:cursor-grabbing"
                  onPointerDown={startPreviewDrag("names")}
                  style={{ transform: `translate(${previewPositions.names.x}px, ${previewPositions.names.y}px)` }}
                >
                  <div className="space-y-2 text-center">
                    <p
                      className="cover-name-3d font-[family-name:var(--font-display)] text-[1.58rem] text-white"
                      style={{
                        fontFamily: groomFormat.fontFamily,
                        fontSize: "1.58rem",
                        fontStyle: groomFormat.italic ? "italic" : "normal",
                        fontWeight: groomFormat.bold ? "700" : "500",
                        textDecoration: groomFormat.underline ? "underline" : "none",
                        textAlign: groomFormat.alignment.toLowerCase() as "left" | "center" | "right",
                      }}
                    >
                      {draft.groom}
                    </p>
                    {hasNamesSeparator ? (
                      <p className="cover-name-3d min-h-[2.5rem] whitespace-pre-wrap text-[2rem] text-white md:text-[2.2rem]">
                        {namesSeparator || "\u00a0"}
                      </p>
                    ) : null}
                    <p
                      className="cover-name-3d font-[family-name:var(--font-display)] text-[1.58rem] text-white"
                      style={{
                        fontFamily: brideFormat.fontFamily,
                        fontSize: "1.58rem",
                        fontStyle: brideFormat.italic ? "italic" : "normal",
                        fontWeight: brideFormat.bold ? "700" : "500",
                        textDecoration: brideFormat.underline ? "underline" : "none",
                        textAlign: brideFormat.alignment.toLowerCase() as "left" | "center" | "right",
                      }}
                    >
                      {draft.bride}
                    </p>
                  </div>
                </div>

                <div
                  className="absolute z-10 cursor-grab touch-none active:cursor-grabbing"
                  onPointerDown={startElementPositionDrag("nameFrame")}
                  style={{
                    left: `${draft.elementPositions.nameFrame.x}%`,
                    top: `${draft.elementPositions.nameFrame.y}%`,
                    width: `${draft.elementSizes.nameFrame}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <Image
                    alt="Decorative name frame"
                    className="h-auto w-full object-contain opacity-95"
                    fill={false}
                    height={110}
                    src="https://theapka.com/storage/templates/golden-elegance//images/name-frame.png"
                    width={320}
                  />
                </div>

                <div
                  className="absolute bottom-[5.9rem] left-5 z-20 w-[calc(100%-2.5rem)] cursor-grab touch-none active:cursor-grabbing"
                  onPointerDown={startPreviewDrag("date")}
                  style={{ transform: `translate(${previewPositions.date.x}px, ${previewPositions.date.y}px)` }}
                >
                  <div className="mx-auto max-w-fit rounded-[1.45rem] border border-white/12 bg-white/10 px-5 py-3 backdrop-blur-sm">
                    <p className="text-[0.72rem] uppercase tracking-[0.3em] text-[#f1d8ab] [text-shadow:0_2px_10px_rgba(0,0,0,0.28)]">
                      Reception Date
                    </p>
                    <p
                      className="mt-2 font-khmer text-sm leading-7 text-white/95 [text-shadow:0_2px_10px_rgba(0,0,0,0.28)]"
                      style={{
                        color: dateLabelFormat.fontColor,
                        fontFamily: dateLabelFormat.fontFamily,
                        fontSize: `${Math.max(Number(dateLabelFormat.fontSize), 14)}px`,
                        fontStyle: dateLabelFormat.italic ? "italic" : "normal",
                        fontWeight: dateLabelFormat.bold ? "700" : "400",
                        textDecoration: dateLabelFormat.underline ? "underline" : "none",
                        textAlign: dateLabelFormat.alignment.toLowerCase() as "left" | "center" | "right",
                      }}
                    >
                      {draft.dateLabel}
                    </p>
                  </div>
                </div>

                <div
                  className="absolute bottom-5 left-1/2 z-20 cursor-grab touch-none active:cursor-grabbing"
                  onPointerDown={startPreviewDrag("button")}
                  style={{ transform: `translate(calc(-50% + ${previewPositions.button.x}px), ${previewPositions.button.y}px)` }}
                >
                  <button className="home-button cover-open-button min-h-0 px-6 py-3 text-[0.78rem]" type="button">
                    <span className="cover-open-button-label">បើកធៀប</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 grid gap-3">
            {(Object.keys(imageFieldLabels) as ImageCardKey[]).map((key) => {
              const labelKey = imageFieldLabels[key];
              const defaultValue = DEFAULT_LANDING_PAGE_CONFIG.images[key];
              const isDefault = draft.images[key] === defaultValue;
              const imagePosition =
                draft.imagePositions[key] ?? DEFAULT_LANDING_PAGE_CONFIG.imagePositions[key];

              return (
                <div
                  key={key}
                  className="rounded-[1.25rem] border border-[color:var(--field-border)] bg-[color:var(--surface)] p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold text-[color:var(--foreground)]">
                      {copy[labelKey]}
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        setDraft((prev) => ({
                          ...prev,
                          images: {
                            ...prev.images,
                            [key]: defaultValue,
                          },
                          imagePositions: {
                            ...prev.imagePositions,
                            [key]: DEFAULT_LANDING_PAGE_CONFIG.imagePositions[key],
                          },
                        }))
                      }
                      className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--muted)] transition hover:text-[color:var(--foreground)]"
                    >
                      {copy.remove}
                    </button>
                  </div>
                  <div className="mt-3 overflow-hidden rounded-[1rem] border border-[color:var(--panel-border)] bg-white">
                    <div
                      className="group relative aspect-square cursor-grab touch-none select-none active:cursor-grabbing"
                      onPointerDown={startImagePositionDrag(key)}
                    >
                      <Image
                        alt={copy[labelKey]}
                        className="h-full w-full object-cover"
                        fill
                        sizes="(max-width: 768px) 100vw, 25vw"
                        src={draft.images[key]}
                        style={{
                          objectPosition: `${imagePosition.x}% ${imagePosition.y}%`,
                        }}
                      />
                      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_42%,rgba(0,0,0,0.16))] opacity-0 transition group-hover:opacity-100" />
                      <div className="pointer-events-none absolute bottom-2 left-2 rounded-full bg-black/45 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/90">
                        Drag to position
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--muted)]">
                      X {Math.round(imagePosition.x)}% / Y {Math.round(imagePosition.y)}%
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setDraft((prev) => ({
                          ...prev,
                          imagePositions: {
                            ...prev.imagePositions,
                            [key]: DEFAULT_LANDING_PAGE_CONFIG.imagePositions[key],
                          },
                        }))
                      }
                      className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--muted)] transition hover:text-[color:var(--foreground)]"
                    >
                      Center
                    </button>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <label className="inline-flex cursor-pointer rounded-full bg-[color:var(--accent)] px-3 py-1.5 text-xs font-semibold text-[color:var(--accent-contrast)] transition hover:brightness-110">
                      {copy.upload}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/jpg"
                        className="hidden"
                        onChange={(event) => void handleImageUpload(event, key)}
                      />
                    </label>
                    {!isDefault ? (
                      <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-[color:var(--accent)]">
                        Custom
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-[2rem] border border-[color:var(--panel-border)] bg-[color:var(--card)] p-5 shadow-[var(--panel-shadow-soft)] sm:p-6">
          <h3 className="text-lg font-semibold text-[color:var(--foreground)]">
            {copy.textsTitle}
          </h3>
          <div className="mt-5 grid gap-4">
            <div className="grid gap-2 rounded-[1.25rem] border border-transparent bg-transparent p-3 text-sm font-medium text-[color:var(--foreground)] transition">
              {language === "kh" ? "ឡូហ្គោ" : "Logo"}
              <LogoUploadCard
                logoPosition={draft.elementPositions.logo}
                logoSrc={draft.images.logo || staticAssets.weddingMark}
                logoSize={draft.elementSizes.logo}
                onFileSelect={(event) => void handleImageUpload(event, "logo")}
                onLogoPositionChange={(position) =>
                  setDraft((prev) => ({
                    ...prev,
                    elementPositions: {
                      ...prev.elementPositions,
                      logo: {
                        x: clampImagePosition(Number.isFinite(position.x) ? position.x : prev.elementPositions.logo.x),
                        y: clampImagePosition(Number.isFinite(position.y) ? position.y : prev.elementPositions.logo.y),
                      },
                    },
                  }))
                }
                onLogoSizeChange={(value) =>
                  setDraft((prev) => ({
                    ...prev,
                    elementSizes: {
                      ...prev.elementSizes,
                      logo: Math.min(140, Math.max(36, Number.isFinite(value) ? value : DEFAULT_LANDING_PAGE_CONFIG.elementSizes.logo)),
                    },
                  }))
                }
                onReset={() =>
                  setDraft((prev) => ({
                    ...prev,
                    images: {
                      ...prev.images,
                      logo: DEFAULT_LANDING_PAGE_CONFIG.images.logo,
                    },
                    elementSizes: {
                      ...prev.elementSizes,
                      logo: DEFAULT_LANDING_PAGE_CONFIG.elementSizes.logo,
                    },
                    elementPositions: {
                      ...prev.elementPositions,
                      logo: DEFAULT_LANDING_PAGE_CONFIG.elementPositions.logo,
                    },
                  }))
                }
                onUrlChange={(value) =>
                  setDraft((prev) => ({
                    ...prev,
                    images: {
                      ...prev.images,
                      logo: value,
                    },
                  }))
                }
                uploadLabel={copy.upload}
                useDefaultLabel={copy.remove}
              />
            </div>

            <div className="grid gap-3 rounded-[1.25rem] border border-[color:var(--field-border)] bg-[color:var(--surface)] p-3 text-sm font-medium text-[color:var(--foreground)]">
              Decorative name frame
              <label className="grid gap-2 text-xs font-semibold text-[color:var(--muted)]">
                Size
                <div className="grid gap-3 sm:grid-cols-[1fr_5rem] sm:items-center">
                  <input
                    type="range"
                    min={30}
                    max={100}
                    value={draft.elementSizes.nameFrame}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        elementSizes: {
                          ...prev.elementSizes,
                          nameFrame: Math.min(100, Math.max(30, Number(event.target.value))),
                        },
                      }))
                    }
                    className="accent-[color:var(--accent)]"
                  />
                  <input
                    type="number"
                    min={30}
                    max={100}
                    value={Math.round(draft.elementSizes.nameFrame)}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        elementSizes: {
                          ...prev.elementSizes,
                          nameFrame: Math.min(100, Math.max(30, Number(event.target.value))),
                        },
                      }))
                    }
                    className="rounded-xl border border-[color:var(--field-border)] bg-[color:var(--card)] px-3 py-2 text-sm text-black outline-none focus:border-[color:var(--accent)]"
                  />
                </div>
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="grid gap-2 text-xs font-semibold text-[color:var(--muted)]">
                  X
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={Math.round(draft.elementPositions.nameFrame.x)}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        elementPositions: {
                          ...prev.elementPositions,
                          nameFrame: {
                            ...prev.elementPositions.nameFrame,
                            x: clampImagePosition(Number(event.target.value)),
                          },
                        },
                      }))
                    }
                    className="rounded-xl border border-[color:var(--field-border)] bg-[color:var(--card)] px-3 py-2 text-sm text-black outline-none focus:border-[color:var(--accent)]"
                  />
                </label>
                <label className="grid gap-2 text-xs font-semibold text-[color:var(--muted)]">
                  Y
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={Math.round(draft.elementPositions.nameFrame.y)}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        elementPositions: {
                          ...prev.elementPositions,
                          nameFrame: {
                            ...prev.elementPositions.nameFrame,
                            y: clampImagePosition(Number(event.target.value)),
                          },
                        },
                      }))
                    }
                    className="rounded-xl border border-[color:var(--field-border)] bg-[color:var(--card)] px-3 py-2 text-sm text-black outline-none focus:border-[color:var(--accent)]"
                  />
                </label>
              </div>
            </div>

            <label
              className={`grid gap-2 rounded-[1.25rem] border p-3 text-sm font-medium transition ${
                activeTextField === "titleBlock"
                  ? "border-[color:var(--accent)] bg-[color:var(--surface-hover)] shadow-[0_0_0_1px_color-mix(in_srgb,var(--accent)_35%,transparent)]"
                  : "border-transparent bg-transparent text-[color:var(--foreground)]"
              }`}
            >
              {language === "kh" ? "ចំណងជើង / ចំណងជើងរង" : "Title / Subtitle"}
              <textarea
                ref={titleBlockRef}
                value={titleBlockValue}
                onChange={(event) => handleTitleBlockChange(event.target.value)}
                onFocus={handleTextFieldFocus("titleBlock")}
                rows={2}
                className="min-h-20 rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-3 text-sm text-black outline-none transition focus:border-[color:var(--accent)]"
                style={{
                  backgroundColor:
                    activeTextField === "titleBlock" ? `${fieldFormats.subtitleKh.highlightColor}22` : undefined,
                  fontFamily: fieldFormats.subtitleKh.fontFamily,
                  fontSize: `${fieldFormats.subtitleKh.fontSize}px`,
                  fontStyle: fieldFormats.subtitleKh.italic ? "italic" : "normal",
                  fontWeight: fieldFormats.subtitleKh.bold ? "700" : "400",
                  textDecoration: fieldFormats.subtitleKh.underline ? "underline" : "none",
                  textAlign: fieldFormats.subtitleKh.alignment.toLowerCase() as "left" | "center" | "right",
                }}
              />
            </label>

            <label
              className={`grid gap-2 rounded-[1.25rem] border p-3 text-sm font-medium transition ${
                activeTextField === "coupleBlock"
                  ? "border-[color:var(--accent)] bg-[color:var(--surface-hover)] shadow-[0_0_0_1px_color-mix(in_srgb,var(--accent)_35%,transparent)]"
                  : "border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)]"
              }`}
            >
              {language === "kh"
                ? "ឈ្មោះកូនប្រុស / ឈ្មោះកូនស្រី"
                : "Groom name / Bride name"}
              <textarea
                ref={coupleBlockRef}
                value={coupleBlockValue}
                onChange={(event) => handleCoupleBlockChange(event.target.value)}
                onFocus={handleTextFieldFocus("coupleBlock")}
                rows={2}
                className="min-h-20 rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--card)] px-4 py-3 text-sm text-black outline-none transition focus:border-[color:var(--accent)]"
                style={{
                  backgroundColor: activeTextField === "coupleBlock" ? `${fieldFormats.groom.highlightColor}22` : undefined,
                  fontFamily: fieldFormats.groom.fontFamily,
                  fontSize: `${fieldFormats.groom.fontSize}px`,
                  fontStyle: fieldFormats.groom.italic ? "italic" : "normal",
                  fontWeight: fieldFormats.groom.bold ? "700" : "400",
                  textDecoration: fieldFormats.groom.underline ? "underline" : "none",
                  textAlign: fieldFormats.groom.alignment.toLowerCase() as "left" | "center" | "right",
                }}
                placeholder={
                  language === "kh"
                    ? "វុឌ្ឍន៏ សច្ចះបូរមី\n&\nខែម និមល"
                    : "Vuth Sachak Boramey\n&\nKhem Nimal"
                }
              />
            </label>

            {textFields
              .filter((field) => field.key !== "titleKh")
              .filter((field) => field.key !== "subtitleKh")
              .filter((field) => !COUPLE_BLOCK_KEYS.includes(field.key as CoupleBlockFieldKey))
              .sort((a, b) => {
                const order: Partial<Record<TextFieldKey, number>> = {
                  dateLabel: 3,
                  timeLabel: 4,
                  venueKh: 5,
                  mapUrl: 6,
                  musicUrl: 7,
                  invitationMessage: 8,
                  thankYouMessage: 9,
                  groom: 10,
                  bride: 11,
                };
                return (order[a.key] ?? 99) - (order[b.key] ?? 99);
              })
              .map((field) => (
                <label
                  key={field.key}
                  className={`grid gap-2 rounded-[1.25rem] border p-3 text-sm font-medium transition ${
                    activeTextField === field.key
                      ? "border-[color:var(--accent)] bg-[color:var(--surface-hover)] shadow-[0_0_0_1px_color-mix(in_srgb,var(--accent)_35%,transparent)]"
                      : "border-transparent bg-transparent text-[color:var(--foreground)]"
                  }`}
                >
                  {language === "kh" ? field.labelKh : field.labelEn}
                  {field.multiline ? (
                    <textarea
                      ref={(node) => {
                        textInputRefs.current[field.key] = node;
                      }}
                      value={draft[field.key]}
                      onChange={(event) => handleTextChange(field.key, event.target.value)}
                      onFocus={handleTextFieldFocus(field.key)}
                      rows={4}
                      className="min-h-28 rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-3 text-sm text-black outline-none transition focus:border-[color:var(--accent)]"
                      style={{
                        backgroundColor:
                          activeTextField === field.key
                            ? `${fieldFormats[field.key].highlightColor}22`
                            : undefined,
                        fontFamily: fieldFormats[field.key].fontFamily,
                        fontSize: `${fieldFormats[field.key].fontSize}px`,
                        fontStyle: fieldFormats[field.key].italic ? "italic" : "normal",
                        fontWeight: fieldFormats[field.key].bold ? "700" : "400",
                        textDecoration: fieldFormats[field.key].underline ? "underline" : "none",
                        textAlign: fieldFormats[field.key].alignment.toLowerCase() as "left" | "center" | "right",
                      }}
                    />
                  ) : (
                    <input
                      ref={(node) => {
                        textInputRefs.current[field.key] = node;
                      }}
                      value={draft[field.key]}
                      onChange={(event) => handleTextChange(field.key, event.target.value)}
                      onFocus={handleTextFieldFocus(field.key)}
                      className="rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-3 text-sm text-black outline-none transition focus:border-[color:var(--accent)]"
                      style={{
                        backgroundColor:
                          activeTextField === field.key
                            ? `${fieldFormats[field.key].highlightColor}22`
                            : undefined,
                        fontFamily: fieldFormats[field.key].fontFamily,
                        fontSize: `${fieldFormats[field.key].fontSize}px`,
                        fontStyle: fieldFormats[field.key].italic ? "italic" : "normal",
                        fontWeight: fieldFormats[field.key].bold ? "700" : "400",
                        textDecoration: fieldFormats[field.key].underline ? "underline" : "none",
                        textAlign: fieldFormats[field.key].alignment.toLowerCase() as "left" | "center" | "right",
                      }}
                    />
                  )}
                </label>
              ))}
          </div>
        </div>
      </section>

      <section className="grid items-start gap-6 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <div className="hidden">
          <h3 className="text-lg font-semibold text-[color:var(--foreground)]">
            {copy.imagesTitle}
          </h3>
          <p className="mt-1 text-xs text-[color:var(--muted)]">{copy.imageHint}</p>
          <div className="mt-4 grid gap-3">
            {(Object.keys(imageFieldLabels) as ImageCardKey[]).map((key) => {
              const labelKey = imageFieldLabels[key];
              const defaultValue = DEFAULT_LANDING_PAGE_CONFIG.images[key];
              const isDefault = draft.images[key] === defaultValue;
              const imagePosition =
                draft.imagePositions[key] ?? DEFAULT_LANDING_PAGE_CONFIG.imagePositions[key];

              return (
                <div
                  key={key}
                  className="rounded-[1.25rem] border border-[color:var(--field-border)] bg-[color:var(--surface)] p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold text-[color:var(--foreground)]">
                      {copy[labelKey]}
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        setDraft((prev) => ({
                          ...prev,
                          images: {
                            ...prev.images,
                            [key]: defaultValue,
                          },
                          imagePositions: {
                            ...prev.imagePositions,
                            [key]: DEFAULT_LANDING_PAGE_CONFIG.imagePositions[key],
                          },
                        }))
                      }
                      className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--muted)] transition hover:text-[color:var(--foreground)]"
                    >
                      {copy.remove}
                    </button>
                  </div>
                  <div className="mt-3 overflow-hidden rounded-[1rem] border border-[color:var(--panel-border)] bg-white">
                    <div
                      className="group relative aspect-square cursor-grab touch-none select-none active:cursor-grabbing"
                      onPointerDown={startImagePositionDrag(key)}
                    >
                      <Image
                        alt={copy[labelKey]}
                        className="h-full w-full object-cover"
                        fill
                        sizes="(max-width: 768px) 100vw, 25vw"
                        src={draft.images[key]}
                        style={{
                          objectPosition: `${imagePosition.x}% ${imagePosition.y}%`,
                        }}
                      />
                      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_42%,rgba(0,0,0,0.16))] opacity-0 transition group-hover:opacity-100" />
                      <div className="pointer-events-none absolute bottom-2 left-2 rounded-full bg-black/45 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/90">
                        Drag to position
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--muted)]">
                      X {Math.round(imagePosition.x)}% / Y {Math.round(imagePosition.y)}%
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setDraft((prev) => ({
                          ...prev,
                          imagePositions: {
                            ...prev.imagePositions,
                            [key]: DEFAULT_LANDING_PAGE_CONFIG.imagePositions[key],
                          },
                        }))
                      }
                      className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--muted)] transition hover:text-[color:var(--foreground)]"
                    >
                      Center
                    </button>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <label className="inline-flex cursor-pointer rounded-full bg-[color:var(--accent)] px-3 py-1.5 text-xs font-semibold text-[color:var(--accent-contrast)] transition hover:brightness-110">
                      {copy.upload}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/jpg"
                        className="hidden"
                        onChange={(event) => void handleImageUpload(event, key)}
                      />
                    </label>
                    {!isDefault ? (
                      <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-[color:var(--accent)]">
                        Custom
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-[2rem] border border-[color:var(--panel-border)] bg-[color:var(--card)] p-5 shadow-[var(--panel-shadow-soft)] sm:p-6">
          <h3 className="text-lg font-semibold text-[color:var(--foreground)]">
            {copy.timelineTitle}
          </h3>
          <div className="mt-4 grid gap-2">
            {draft.timeline.map((item, index) => (
              <div
                key={item.id}
                className="grid gap-2 rounded-[1rem] border border-[color:var(--field-border)] bg-[color:var(--surface)] p-3"
              >
                <div className="grid gap-2 sm:grid-cols-[5.25rem_1fr]">
                  <input
                    value={item.time}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        timeline: prev.timeline.map((row, rowIndex) =>
                          rowIndex === index ? { ...row, time: event.target.value } : row,
                        ),
                      }))
                    }
                    className="rounded-xl border border-[color:var(--field-border)] bg-[color:var(--card)] px-3 py-2 text-xs text-black outline-none transition focus:border-[color:var(--accent)]"
                  />
                  <input
                    value={item.title}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        timeline: prev.timeline.map((row, rowIndex) =>
                          rowIndex === index ? { ...row, title: event.target.value } : row,
                        ),
                      }))
                    }
                    className="rounded-xl border border-[color:var(--field-border)] bg-[color:var(--card)] px-3 py-2 text-xs text-black outline-none transition focus:border-[color:var(--accent)]"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[2rem] border border-[color:var(--panel-border)] bg-[color:var(--card)] p-5 shadow-[var(--panel-shadow-soft)] sm:p-6">
          <h3 className="text-lg font-semibold text-[color:var(--foreground)]">
            {copy.galleryTitle}
          </h3>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
            {gallerySlots.map((image, index) => (
              <div
                key={`${index}-${image.slice(0, 24)}`}
                className="rounded-[1.25rem] border border-[color:var(--field-border)] bg-[color:var(--surface)] p-3"
              >
                <div className="relative aspect-square overflow-hidden rounded-[1rem] border border-[color:var(--panel-border)] bg-white">
                  <Image
                    alt={`Gallery ${index + 1}`}
                    className="h-full w-full object-cover"
                    fill
                    sizes="(max-width: 768px) 100vw, 20vw"
                    src={image}
                  />
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <label className="inline-flex cursor-pointer rounded-full bg-[color:var(--accent)] px-3 py-1.5 text-xs font-semibold text-[color:var(--accent-contrast)] transition hover:brightness-110">
                    {copy.upload}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/jpg"
                      className="hidden"
                      onChange={(event) => void handleGalleryUpload(event, index)}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setDraft((prev) => {
                        const nextGallery = [...prev.images.gallery];
                        nextGallery[index] =
                          DEFAULT_LANDING_PAGE_CONFIG.images.gallery[index] ??
                          DEFAULT_LANDING_PAGE_CONFIG.images.gallery[0];
                        return {
                          ...prev,
                          images: {
                            ...prev.images,
                            gallery: nextGallery,
                          },
                        };
                      })
                    }
                    className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--muted)] transition hover:text-[color:var(--foreground)]"
                  >
                    {copy.remove}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <HomeStyles />
    </div>
  );
}
